import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Pencil, Trash2, Plus, Languages, X } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomPagination from '../../components/common/pagination';
import SearchBar from '../../components/SearchBar';
import DeleteModal from '../../components/deleteModal';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import EmojiTextField from '../../components/common/EmojiTextField';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { translateText, translateToAllLanguages } from '../../utils/translateText';
import { LANGUAGES, getSituationField } from '../../constants/languages';
import {
    createSituation,
    getSituations,
    updateSituation,
    deleteSituation,
    deleteMultipleSituations,
} from '../../api/situationapi';
import CustomCheckbox from '../../components/common/CustomCheckbox';

const LIMIT = 15;

const initialFormState = LANGUAGES.reduce((acc, lang) => {
    acc[getSituationField(lang.code)] = '';
    return acc;
}, {});

const Situation = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [formData, setFormData] = useState(initialFormState);
    const [editId, setEditId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const [translatingLang, setTranslatingLang] = useState(null);
    const [translatingAll, setTranslatingAll] = useState(false);

    const [deleteIds, setDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);

    // languages column na badha scrollers ne sync rakhva (ek scroll kare to badha sathe hale)
    const scrollersRef = useRef(new Set());
    const lastScrollLeftRef = useRef(0);

    const hasActiveFilters = Boolean(searchInput.trim());
    const getId = (item) => item._id || item.id;

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const loadItems = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getSituations({ page: currentPage, limit: LIMIT, search });
            setItems(res?.data || []);
            setTotalPages(res?.totalPages || 1);
            setTotalItems(res?.total || 0);
            setSelectedIds([]);
        } catch (error) {
            toast.error('Failed to load situations!');
        } finally {
            setLoading(false);
        }
    }, [currentPage, search]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleClearAllFilters = () => {
        setSearchInput('');
        setSearch('');
        setCurrentPage(1);
    };

    /* ---------------- languages column scroll sync ---------------- */

    const registerScroller = (el) => {
        if (el) {
            scrollersRef.current.add(el);
            el.scrollLeft = lastScrollLeftRef.current;
        }
    };

    const handleLangScroll = (e) => {
        const left = e.currentTarget.scrollLeft;
        if (left === lastScrollLeftRef.current) return;
        lastScrollLeftRef.current = left;

        scrollersRef.current.forEach((el) => {
            if (!el.isConnected) {
                scrollersRef.current.delete(el);
            } else if (el !== e.currentTarget && el.scrollLeft !== left) {
                el.scrollLeft = left;
            }
        });
    };

    /* ---------------- selection ---------------- */

    const allSelected = items.length > 0 && items.every((item) => selectedIds.includes(getId(item)));

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? [] : items.map(getId));
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /* ---------------- modal ---------------- */

    const openAddModal = () => {
        setModalMode('add');
        setFormData(initialFormState);
        setEditId(null);
        setErrors({});
        setIsModalOpen(true);
    };

    const openEditModal = (item) => {
        setModalMode('edit');

        const next = {};
        LANGUAGES.forEach(({ code }) => {
            const field = getSituationField(code);
            next[field] = item[field] || '';
        });

        setFormData(next);
        setEditId(getId(item));
        setErrors({});
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (submitting || translatingAll) return;
        setIsModalOpen(false);
        setFormData(initialFormState);
        setEditId(null);
        setErrors({});
    };

    const clearFieldError = (field) => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    /* ---------------- translation ---------------- */

    const handleTranslateOne = async (lang) => {
        const english = formData.enSituation.trim();
        if (!english) {
            toast.warning('Please enter the English situation first.');
            return;
        }

        setTranslatingLang(lang.code);
        try {
            const translated = await translateText(english, lang.code);
            handleFieldChange(getSituationField(lang.code), translated);
        } catch (error) {
            toast.error(`Failed to translate to ${lang.name}. Please try again.`);
        } finally {
            setTranslatingLang(null);
        }
    };

    const handleTranslateAll = async () => {
        const english = formData.enSituation.trim();
        if (!english) {
            toast.warning('Please enter the English situation first.');
            return;
        }

        const targets = LANGUAGES.filter((l) => l.code !== 'en');

        setTranslatingAll(true);
        try {
            const { translations, failed } = await translateToAllLanguages(
                english,
                targets.map((l) => l.code)
            );

            setFormData((prev) => {
                const next = { ...prev };
                Object.entries(translations).forEach(([code, text]) => {
                    next[getSituationField(code)] = text;
                });
                return next;
            });

            Object.keys(translations).forEach((code) => clearFieldError(getSituationField(code)));

            if (failed.length) {
                const names = targets.filter((l) => failed.includes(l.code)).map((l) => l.name).join(', ');
                toast.warning(`Could not translate: ${names}. Retry those or type them manually.`);
            } else {
                toast.success('Translated to all languages!');
            }
        } finally {
            setTranslatingAll(false);
        }
    };

    /* ---------------- submit ---------------- */

    const validate = () => {
        const errs = {};
        LANGUAGES.forEach(({ code, name }) => {
            const field = getSituationField(code);
            if (!formData[field].trim()) errs[field] = `${name} situation is required`;
        });
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setSubmitting(true);
            if (modalMode === 'add') {
                await createSituation(formData);
                toast.success('Situation added successfully!');
            } else {
                await updateSituation(editId, formData);
                toast.success('Situation updated successfully!');
            }
            setIsModalOpen(false);
            setFormData(initialFormState);
            setEditId(null);
            setErrors({});

            if (modalMode === 'add' && currentPage !== 1) {
                setCurrentPage(1);
            } else {
                loadItems();
            }
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                (modalMode === 'add' ? 'Failed to add situation!' : 'Failed to update situation!');
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------------- delete (single + multiple) ---------------- */

    const confirmDelete = (id) => setDeleteIds([id]);
    const confirmDeleteSelected = () => setDeleteIds(selectedIds);
    const cancelDelete = () => {
        if (!deleting) setDeleteIds([]);
    };

    const handleDelete = async () => {
        if (deleteIds.length === 0) return;

        try {
            setDeleting(true);

            if (deleteIds.length === 1) {
                await deleteSituation(deleteIds[0]);
                toast.success('Situation deleted successfully!');
            } else {
                const res = await deleteMultipleSituations(deleteIds);
                toast.success(res?.message || `${deleteIds.length} situations deleted successfully!`);
            }

            const deletedWholePage = deleteIds.length >= items.length;
            setDeleteIds([]);

            if (deletedWholePage && currentPage > 1) {
                setCurrentPage((p) => p - 1);
            } else {
                loadItems();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete!');
        } finally {
            setDeleting(false);
        }
    };

    /* ---------------- render ---------------- */

    const colCount = 4; // checkbox + index + languages + actions

    const headBase =
        'py-3 px-2 text-center font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700';
    const cellBase =
        'py-3 px-3 align-middle text-gray-700 dark:text-gray-300 border-r border-b border-gray-200 dark:border-gray-700';

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] min-h-0 overflow-hidden">
            {/* Header */}
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
                <PageBreadcrumb pageTitle="Situations" />

                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={openAddModal}
                        className="inline-flex items-center gap-2 justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white"
                    >
                        <Plus size={16} /> Add Situation
                    </button>
                </div>
            </div>

            {/* Table area */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="flex h-full min-h-0 flex-col rounded-2xl border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
                    <div className="flex shrink-0 items-center justify-between">
                        <SearchBar
                            value={searchInput}
                            onChange={setSearchInput}
                            placeholder="Search in any language..."
                            showClearAll={hasActiveFilters}
                            onClearAll={handleClearAllFilters}
                        />

                        {selectedIds.length > 0 && (
                            <button
                                onClick={confirmDeleteSelected}
                                className="inline-flex items-center gap-2 justify-center rounded-lg bg-red-500 px-4 py-2 mx-3 text-sm font-medium text-white hover:bg-red-600"
                            >
                                <Trash2 size={16} /> Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    <div className="flex-1 min-h-0 overflow-hidden p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6 pt-4">
                        {/* vertical scroll fakt; horizontal scroll languages column ni andar */}
                        <div className="h-full min-h-0 rounded-lg border dark:border-gray-800 overflow-y-auto overflow-x-hidden">
                            <table className="w-full table-fixed">
                                <colgroup>
                                    <col style={{ width: 48 }} />
                                    <col style={{ width: 64 }} />
                                    <col />
                                    <col style={{ width: 96 }} />
                                </colgroup>

                                <thead className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-white/[0.05] dark:bg-gray-900">
                                    <tr>
                                        <th className={headBase}>
                                            <CustomCheckbox
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                disabled={items.length === 0}
                                            />
                                        </th>
                                        <th className={headBase}>Index</th>
                                        <th className={headBase}>Languages</th>
                                        <th className={`${headBase} border-r-0`}>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : items.length === 0 ? (
                                        <tr>
                                            <td colSpan={colCount} className="py-6 px-2 text-center text-gray-500 dark:text-gray-400">
                                                No data found.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => {
                                            const id = getId(item);
                                            return (
                                                <tr key={id} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                                                    <td className={`${cellBase} text-center`}>
                                                        <CustomCheckbox
                                                            checked={selectedIds.includes(id)}
                                                            onChange={() => toggleSelectOne(id)}
                                                        />
                                                    </td>

                                                    <td className={`${cellBase} text-center`}>
                                                        {(currentPage - 1) * LIMIT + index + 1}
                                                    </td>

                                                    {/* Languages: ek j column, andar horizontal scroll */}
                                                    <td className={`${cellBase} !p-0 overflow-hidden`}>
                                                        <div
                                                            ref={registerScroller}
                                                            onScroll={handleLangScroll}
                                                            className="overflow-x-auto"
                                                        >
                                                            <div className="flex w-max">
                                                                {LANGUAGES.map((lang) => {
                                                                    const value = item[getSituationField(lang.code)];
                                                                    return (
                                                                        <div
                                                                            key={lang.code}
                                                                            className="w-[240px] text-center shrink-0 border-r border-gray-100 px-3 py-3 last:border-r-0 dark:border-white/[0.05]"
                                                                        >
                                                                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-brand-500">
                                                                                {lang.name}
                                                                            </div>
                                                                            {value ? (
                                                                                <div className="flex items-start justify-center gap-2 text-left">
                                                                                    <span className="whitespace-normal break-words">{value}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => copyToClipboard(value, `${lang.name} situation`)}
                                                                                        title="Copy"
                                                                                        className="mt-1 text-gray-400 hover:text-brand-500 transition-colors shrink-0"
                                                                                    >
                                                                                        <Copy size={14} className='mt-1' />
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-gray-400 text-xs italic">—</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className={`${cellBase} text-center border-r-0`}>
                                                        <div className="flex items-center justify-center gap-3">
                                                            <button onClick={() => openEditModal(item)} title="Edit" className="text-brand-600">
                                                                <Pencil size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => confirmDelete(id)}
                                                                title="Delete"
                                                                className="text-red-500 hover:text-red-600"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="shrink-0 w-full border-t dark:bg-gray-900 dark:border-gray-700 mt-0">
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={LIMIT}
                    totalItems={totalItems}
                />
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/50 px-4">
                    <div
                        className="relative w-full max-w-lg max-h-[95vh] overflow-y-auto rounded-xl bg-white p-6 dark:bg-gray-900"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {modalMode === 'add' ? 'Add Situation' : 'Edit Situation'}
                            </h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={submitting || translatingAll}
                                title="Close"
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 dark:text-gray-400 dark:hover:bg-white/[0.05] dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {LANGUAGES.map((lang) => {
                                const field = getSituationField(lang.code);
                                const isEnglish = lang.code === 'en';

                                return (
                                    <React.Fragment key={lang.code}>
                                        <EmojiTextField
                                            label={`Situation (${lang.name})`}
                                            required
                                            value={formData[field]}
                                            onChange={(v) => handleFieldChange(field, v)}
                                            error={errors[field]}
                                            disabled={submitting || translatingAll}
                                            {...(!isEnglish && {
                                                actionLabel: `Get ${lang.name}`,
                                                onAction: () => handleTranslateOne(lang),
                                                isActionLoading: translatingLang === lang.code,
                                            })}
                                        />

                                        {isEnglish && (
                                            <button
                                                type="button"
                                                onClick={handleTranslateAll}
                                                disabled={submitting || translatingAll}
                                                className="mb-4 inline-flex items-center gap-2 rounded-lg border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-500 disabled:opacity-60"
                                            >
                                                <Languages size={16} />
                                                {translatingAll ? 'Translating...' : 'Translate to all languages'}
                                            </button>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={submitting || translatingAll}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || translatingAll}
                                    className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                                >
                                    {submitting ? 'Saving...' : modalMode === 'add' ? 'Add' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DeleteModal
                isOpen={deleteIds.length > 0}
                value={deleteIds.length > 1 ? `${deleteIds.length} situations` : 'situation'}
                isDeleting={deleting}
                onClose={cancelDelete}
                onConfirm={handleDelete}
            />

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default Situation;